import { ComponentProps } from "react";

export type ChipOptionBase = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type Props = Omit<ComponentProps<"div">, "onChange"> & {
  /** Field name for identification */
  name: string;
  /** Available options */
  options: ChipOptionBase[];
  /** Currently selected values */
  value?: string[];
  /** Change handler - receives array of selected values */
  onChange?: (value: string[]) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error (for styling and aria-invalid) */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Maximum number of items that can be selected (default: Infinity) */
  maxItems?: number;
  /** ID of the element that describes this group (typically error message) */
  "aria-describedby"?: string;
  /** Called when the user tries to select more items than maxItems allows */
  onMaxItemsExceeded?: (maxItems: number) => void;
  /** Render function for each chip */
  renderChip?: (props: {
    option: ChipOptionBase;
    isChecked: boolean;
    isDisabled: boolean;
    onChange: (checked: boolean) => void;
  }) => React.ReactNode;
};

/**
 * Base select chips component with no styling.
 * Use this as the foundation for styled select chips components.
 *
 * Supports single-select (maxItems=1) and multi-select (maxItems>1) modes.
 */
export function SelectChipsBase({
  name,
  options,
  value = [],
  onChange,
  onBlur,
  error,
  disabled = false,
  maxItems = Infinity,
  onMaxItemsExceeded,
  renderChip,
  className,
  id,
  ...props
}: Props) {
  const isMultiSelect = maxItems > 1;
  const selectedValues = Array.isArray(value) ? value : [];

  const handleChipChange = (optionValue: string, checked: boolean) => {
    let newValue: string[];

    if (checked) {
      if (isMultiSelect) {
        if (selectedValues.length >= maxItems) {
          onMaxItemsExceeded?.(maxItems);
          return;
        }
        newValue = [...selectedValues, optionValue];
      } else {
        newValue = [optionValue];
      }
    } else {
      newValue = selectedValues.filter((val) => val !== optionValue);
    }

    onChange?.(newValue);
  };

  // Default render if no custom render function provided
  const defaultRenderChip = ({
    option,
    isChecked,
    isDisabled,
    onChange: onChipChange,
  }: {
    option: ChipOptionBase;
    isChecked: boolean;
    isDisabled: boolean;
    onChange: (checked: boolean) => void;
  }) => (
    <button
      key={option.value}
      type="button"
      onClick={() => onChipChange(!isChecked)}
      disabled={isDisabled}
      aria-pressed={isChecked}
      data-testid={`chip-${name}-${option.value}`}
    >
      {option.label}
    </button>
  );

  const render = renderChip ?? defaultRenderChip;

  return (
    <div
      role="group"
      aria-labelledby={id ? `${id}-label` : undefined}
      aria-invalid={error || undefined}
      className={className}
      onBlur={onBlur}
      data-testid={`selectchips-${name}`}
      {...props}
    >
      {options.map((option) => {
        const isChecked = selectedValues.includes(option.value);
        const isDisabled = disabled || !!option.disabled;

        return render({
          option,
          isChecked,
          isDisabled,
          onChange: (checked) => handleChipChange(option.value, checked),
        });
      })}
    </div>
  );
}

SelectChipsBase.displayName = "SelectChipsBase";

